import { Virtualizer } from '@tanstack/react-virtual';
import { useCallback } from 'react';
import { EDITOR_CLIP_SCROLL_POSITION_FROM_TOP_PCT } from 'config';
import { useAudioPlayer } from 'pages/TranscriptEditorPage/AudioPlayerContext';
import {
  useSegmentsById,
  useWordsById,
} from 'pages/TranscriptEditorPage/state';
import {
  EditorUiStore,
  useEditorUiSubscription,
} from 'pages/TranscriptEditorPage/state/editorUi';
import {
  actionsSelector,
  isScrollingSegmentSelector,
  scrollRequestSelector,
  scrollToWordContextSelector,
  scrollToWordIdSelector,
} from 'pages/TranscriptEditorPage/state/editorUi/selectors';
import { getWordElement } from 'pages/TranscriptEditorPage/utils';
import { getDistanceFromCenterOfScreen, isInViewport } from 'utils/dom';

export interface UseScrollIntoViewConfig {
  virtualizer: Virtualizer<Window, Element>;
}

export default function useScrollIntoView({
  virtualizer,
}: UseScrollIntoViewConfig) {
  const { player } = useAudioPlayer();
  const { data: wordsById } = useWordsById();
  const { data: segmentsById } = useSegmentsById();

  useEditorUiSubscription(
    useCallback(
      (current: EditorUiStore, prev: EditorUiStore) => {
        if (!wordsById || !segmentsById) {
          return;
        }

        const { scrollToSegment } = actionsSelector(current);

        const scrollRequest = scrollRequestSelector(current);
        const prevScrollRequest = scrollRequestSelector(prev);
        const hasNewScrollRequest = scrollRequest !== prevScrollRequest;

        const isScrolling = isScrollingSegmentSelector(current);
        const wasScrolling = isScrollingSegmentSelector(prev);
        const hasFinishedScrolling = wasScrolling && !isScrolling;

        if (hasFinishedScrolling || hasNewScrollRequest) {
          const wordId = scrollToWordIdSelector(current) ?? -1;
          const context = scrollToWordContextSelector(current) ?? 'default';
          const word = wordsById[wordId];

          if (!word) {
            return;
          }

          const segment = segmentsById[word.segmentId];

          const {
            startIndex: firstRenderedSegmentIndex,
            endIndex: lastRenderedSegmentIndex,
          } = virtualizer.range;

          // if the scroll word's segment is not rendered, bring it in.
          // that's all we have to do here.  once the segment is in, we state will
          // update indicating that and we should be able to find the scroll element
          // (the else block, below)
          if (
            segment.index < firstRenderedSegmentIndex ||
            segment.index > lastRenderedSegmentIndex
          ) {
            scrollToSegment(word.id);
            virtualizer.scrollToIndex(segment.index);
          } else {
            // word should be on the screen somewhere
            const scrollToElement = getWordElement(word.id);

            // but anything could happen, so check
            if (scrollToElement) {
              const scrollTargetRect = scrollToElement.getBoundingClientRect();
              const scrollTargetCenteringDelta =
                getDistanceFromCenterOfScreen(scrollTargetRect);

              // handle each scroll context.
              // this conditional can probably be simplified but it was written to
              // make it clear how each context is handled
              if (context === 'default') {
                // if the word is not in view or if a segment was just scrolled into
                // view, then bring the word to the center of the screen immediately.
                // also, if the player is playing and we see a "default" event, scroll
                // to it immediately.  smooth scrolling is only for advancing consecutive
                // lines in the center of the screen during playback
                if (
                  !isInViewport(scrollTargetRect) ||
                  hasFinishedScrolling ||
                  !player.paused
                ) {
                  virtualizer.scrollBy(scrollTargetCenteringDelta);
                }
              } else if (context === 'playback') {
                // during "playback", first treat the word the same as the default case.
                // if it's not on screen, get it there.
                if (
                  !isInViewport(scrollTargetRect) ||
                  hasFinishedScrolling ||
                  player.paused
                ) {
                  virtualizer.scrollBy(scrollTargetCenteringDelta);
                } else {
                  // smooth scrolling to advance consecutive lines
                  virtualizer.scrollBy(scrollTargetCenteringDelta, {
                    behavior: 'smooth',
                  });
                }
              } else {
                virtualizer.scrollBy(
                  getDistanceFromCenterOfScreen(
                    scrollTargetRect,
                    -50 + EDITOR_CLIP_SCROLL_POSITION_FROM_TOP_PCT,
                  ),
                );
              }
            }
          }
        }
      },
      [player.paused, segmentsById, virtualizer, wordsById],
    ),
  );
}
